import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import {
  Checkbox,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  IconButton,
  Button,
  FormControlLabel,
  CircularProgress,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteIcon from "@material-ui/icons/Delete";
import GetAppIcon from "@material-ui/icons/GetApp";
import { format, parse } from "date-fns";
import downloadFile from "downloadjs";

import {
  makeGetArchiveTarballs,
  makeGetOneTarball,
  makeDeleteListOfTarballs,
} from "../../backend/zip";
import { TIME__DATE, TIME__DATE_AND_TIME } from "../../utils/constants";

import DialogRender from "./DialogRender";
import { ACTION } from "./constants";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
  },
  archiveItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  accordionItem: {
    display: "flex",
    flexDirection: "column",
  },
  rightShift: {
    marginRight: 10,
  },
  leftShift: {
    marginLeft: 20,
  },
  flexRowCenter: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  btnContainer: {
    marginTop: 20,
    padding: "20px 0",
    display: "flex",
    justifyContent: "end",
  },
  circularProgress: {
    margin: "0 auto",
    display: "block",
  },
}));

const TemplateHistory = () => {
  const classes = useStyles();
  const [archiveList, setArchiveList] = useState([]);
  const [checkedList, setCheckedList] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [currentAction, setCurrentAction] = useState("");
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [loader, setLoader] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    getArchivesList();
  }, []);

  const getArchivesList = async () => {
    setLoader(true);
    try {
      const res = await makeGetArchiveTarballs();
      if (!res.data?.Contents?.length) {
        setArchiveList([]);
        setLoader(false);
        return;
      }
      const restructuredData = res.data.Contents.map(
        ({ LastModified, Key }) => {
          let templateId = Key.replace(
            `templateArchives-${process.env.REACT_APP_ENV}/template - `,
            ""
          );
          if (templateId.includes(".json"))
            templateId = templateId.replace(".json", "");
          const dateOfCreation = format(new Date(LastModified), TIME__DATE);
          const templateName = `template - ${format(
            new Date(templateId),
            TIME__DATE_AND_TIME
          )}`;
          return [dateOfCreation, templateName];
        }
      );

      const groupByDate = {};

      restructuredData.forEach((item) => {
        if (groupByDate.hasOwnProperty(item[0])) {
          groupByDate[item[0]].push(item[1]);
        } else {
          groupByDate[item[0]] = [item[1]];
        }
      });

      //Sorting by time for each day
      for (let key in groupByDate) {
        groupByDate[key].sort((a, b) => {
          const dateA = new Date(a.replace("template - ", ""));
          const dateB = new Date(b.replace("template - ", ""));
          //First - new. Last - old
          return dateB - dateA;
        });
      }

      const sortedListByDate = Object.entries(groupByDate).sort((a, b) => {
        //First - new. Last - old
        return new Date(b[0]) - new Date(a[0]);
      });

      setArchiveList(sortedListByDate);
    } catch (e) {
      console.error(e);
      toast.error(
        <div>
          <p>Something went wrong while loading the list of archives.</p>
        </div>
      );
    }
    setLoader(false);
  };

  const handleCheckboxClick = (templateName) => {
    if (checkedList.indexOf(templateName) > -1) {
      setCheckedList(checkedList.filter((item) => item !== templateName));
    } else {
      setCheckedList([...checkedList, templateName]);
    }
  };

  const handleDownloadButton = (list) => {
    setLoader(true);
    try {
      list.map((item) => {
        const templateId = item.replace("template - ", "");
        const generatedItem = `templateArchives-${
          process.env.REACT_APP_ENV
        }/template - ${parse(
          templateId,
          TIME__DATE_AND_TIME,
          new Date()
        ).toISOString()}`;
        return makeGetOneTarball(generatedItem).then((response) => {
          const file = new File([response.data], item);
          downloadFile(file, `${file.name}.json`, "application/json");
        });
      });
    } catch (e) {
      console.error(e);
      toast.error(
        <div>
          <p>Something went wrong while loading the archive.</p>
        </div>
      );
    }
    setLoader(false);
  };

  const handleDeleteButton = async (list) => {
    setLoader(true);
    try {
      const resp = await makeDeleteListOfTarballs(
        list.map((item) => {
          const templateId = item.replace("template - ", "");
          const generatedItem = `templateArchives-${
            process.env.REACT_APP_ENV
          }/template - ${parse(
            templateId,
            TIME__DATE_AND_TIME,
            new Date()
          ).toISOString()}`;

          return generatedItem;
        })
      );
      if (resp.status === 200) {
        getArchivesList();
      }
    } catch (e) {
      console.error(e);
      toast.error(
        <div>
          <p>Something went wrong while deleting archives.</p>
        </div>
      );
    }
    setLoader(false);
  };

  const accordionItem = ([date, list]) => {
    const checkedCurrentList = checkedList.filter((item) =>
      item.includes(date)
    );
    const isChecked =
      checkedCurrentList.filter((item) => item.includes(date)).length ===
      list.length;
    const handleCheckboxGroupClick = (event) => {
      event.stopPropagation();
      if (isChecked) {
        setCheckedList(checkedList.filter((x) => !list.includes(x)));
      } else {
        setCheckedList(Array.from(new Set([...checkedList, ...list])));
      }
    };

    return (
      <Accordion
        key={date}
        expanded={expanded === date}
        onChange={handleChange(date)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
        >
          <div className={classes.flexRowCenter}>
            <FormControlLabel
              onClick={(event) => event.stopPropagation()}
              onFocus={(event) => event.stopPropagation()}
              control={
                <Checkbox
                  onClick={handleCheckboxGroupClick}
                  checked={isChecked}
                />
              }
              label={
                <Typography className={classes.heading}>{date}</Typography>
              }
            />
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionItem}>
          {list.map((archiveName) => archiveItem(archiveName))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const archiveItem = (archive) => {
    return (
      <div key={archive} className={classes.archiveItem}>
        <div className={classes.archiveItem}>
          <Checkbox
            className={classes.leftShift}
            onClick={() => handleCheckboxClick(archive)}
            checked={checkedList.indexOf(archive) > -1}
          />
          <Typography>{archive}</Typography>
        </div>
        <div>
          <IconButton
            className={classes.rightShift}
            onClick={() => handleDownloadButton([archive])}
          >
            <GetAppIcon />
          </IconButton>
          <IconButton
            className={classes.rightShift}
            onClick={() => {
              setCheckedList([archive]);
              setCurrentAction(ACTION.delete);
              setDialogIsOpen(true);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      </div>
    );
  };

  return (
    <div className="accordion">
      <DialogRender
        checkedList={checkedList}
        dialogIsOpen={dialogIsOpen}
        currentAction={currentAction}
        setCheckedList={setCheckedList}
        setDialogIsOpen={setDialogIsOpen}
        setCurrentAction={setCurrentAction}
        handleDeleteButton={handleDeleteButton}
        handleDownloadButton={handleDownloadButton}
      />
      <div className={classes.root}>
        {!loader ? (
          archiveList.map((item, index) => accordionItem(item))
        ) : (
          <CircularProgress className={classes.circularProgress} />
        )}
        <div className={classes.btnContainer}>
          <Button
            variant="contained"
            color="primary"
            className={classes.rightShift}
            disabled={!checkedList.length}
            onClick={() => {
              setCurrentAction(ACTION.download);
              setDialogIsOpen(true);
            }}
          >
            Download
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!checkedList.length}
            onClick={() => {
              setCurrentAction(ACTION.delete);
              setDialogIsOpen(true);
            }}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplateHistory;
